import React, { useState, useContext, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import Dialog from "@mui/material/Dialog";
import { UserCtx } from "../../../interfaces";
import { UserContext } from "../../../context/UserContext";
import { Typography, Button, Stack } from "@mui/material";

const useStyles = makeStyles((theme) => ({
  newPosOfDialog: {
    padding: 32,
    maxWidth: 500,
    minHeight: 200,
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: 15,
  },
  surveyDialog: {
    padding: "32px 0px",
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: 15,
  },
  dialogTitle: {
    marginBottom: "24px",
  },
  dialogText: {
    marginBottom: "33px",
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  buttonWrapper: {
    display: "flex",
    justifyContent: "flex-start",
  },
  bottomButton: {
    fontSize: "16px",
    borderRadius: "36px",
    borderColor: "#191919",
    paddingLeft: "24px",
    paddingRight: "24px",
    paddingTop: "12px",
    paddingBottom: "12px",
    borderStyle: "solid",
    borderWidth: "1px",
    cursor: "pointer",
    marginRight: "16px",

    "&:hover": {
      cursor: "pointer",
    },
  },
  buttonNo: {
    width: 210,
    backgroundColor: "#D8D8D8",
    color: theme.palette.text.primary,
    "&:hover": {
      backgroundColor: "#C9C8C8",
    },
  },
  buttonYes: { width: 210, padding: 0 },
}));

export interface SurveyModalProps {
  dialogTitle: string;
  dialogBody: string;

  surveyField: string;
  surveyUrl: string;

  promptOpen: boolean;
  promptOnClose: () => void;
}

export const SurveyModal = (props: SurveyModalProps) => {
  const classes = useStyles();
  const { promptOnClose, promptOpen, dialogTitle, dialogBody, surveyField, surveyUrl } =
    props;
  const userCtx = useContext(UserContext) as UserCtx;
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [userId, setUserId] = useState("");

  useEffect(() => {
    if (!userCtx.dbUser || !userCtx.dbUser.id) return;
    setUserId(userCtx.dbUser.id);
  }, [userCtx.dbUser]);

  useEffect(() => {
    const handleFinishSurvey = async () => {
      switch (surveyField) {
        case "surveyed_pre_course":
          await userCtx.upsertUserSurvey({
            surveyed_pre_course: true,
            pre_survey_date: new Date(),
          });
          break;
        case "times_surveyed_mid_course":
          await userCtx.upsertUserSurvey({
            last_mid_survey_date: new Date(),
            times_surveyed_mid_course:
              (userCtx.dbUser?.user_survey?.times_surveyed_mid_course ?? 0) + 1,
          });
          break;
        default:
          await userCtx.upsertUserSurvey({
            [surveyField]: true,
          });
          break;
      }

      promptOnClose();
      setSurveyOpen(false);
    };

    const recieveMessage = async (event: MessageEvent) => {
      console.log("POST MESSAGE EVENT", event);
      if (surveyOpen) {
        if (event.data === "End of Survey" || event.data === "endOfSurvey") {
          setTimeout(
            (ref) => {
              ref();
            },
            3000,
            handleFinishSurvey
          );
        }
      }
    };
    window.addEventListener("message", recieveMessage);
    return () => window.removeEventListener("message", recieveMessage);
  }, [surveyOpen, userCtx, promptOnClose, surveyField]);

  const handleOpenSurveyModal = () => {
    promptOnClose();
    setSurveyOpen(true);
  };

  // Commented out, current iteration of surveys should not be dismissable
  // const handleDismissPrompt = () => {
  //   promptOnClose();
  //   setSurveyOpen(false);
  //   // userCtx.upsertUserSurvey({
  //   //   consent_dismissals: (userCtx.dbUser?.user_survey?.consent_dismissals ?? 0) + 1,
  //   // });
  // };

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="sm"
        classes={{
          paper: classes.newPosOfDialog,
        }}
        aria-labelledby="simple-dialog-title"
        open={promptOpen}>
        <Typography variant="h4" className={classes.dialogTitle}>
          {dialogTitle}
        </Typography>

        <Typography variant="body2" className={classes.dialogText}>
          {dialogBody}
        </Typography>
        <Stack direction="row" justifyContent="center">
          <Button
            onClick={handleOpenSurveyModal}
            variant="contained"
            color="primary"
            size="large"
            className={classes.buttonYes}>
            Let’s Go!
          </Button>
        </Stack>
      </Dialog>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={surveyOpen}
        classes={{
          paper: classes.surveyDialog,
        }}>
        <iframe
          title="Qualtrics Survey"
          src={`${surveyUrl}?user_id=${userId}${
            surveyField === "times_surveyed_mid_course"
              ? `&times_surveyed_mid_course=${
                  userCtx.dbUser?.user_survey?.times_surveyed_mid_course ?? 0
                }`
              : ""
          }`}
          frameBorder="0"
          allow="fullscreen"
          lang="en"
          style={{ width: "100%", height: "80vh" }}
        />
      </Dialog>
    </>
  );
};
